import React, { useState, useMemo, useEffect } from 'react';
import { useApiSearch } from 'hooks/useApiSearch';
import { MultiSearchDropdown } from './';
import { IMultiSearchDropdownOption } from './MultiSearchDropdownOptions';

export interface IMultiSearchDropdownApiProps {
  // Core props (passed through to MultiSearchDropdown)
  fieldId: string;
  values: string[];
  onValueChange: (values: string[]) => void;
  label?: string;
  className?: string;
  inputClassName?: string;
  maxVisibleItems?: number;
  withTags?: boolean;
  disabled?: boolean;
  errorMessage?: string | null;
  errorClassName?: string;
  inputPlaceholder?: string;

  /**
   *
   * @param searchTerm The search terms that the user has typed in
   * @param signal An abort signal if you want to use it
   * @returns
   */
  searchFn: (searchTerm: string, signal?: AbortSignal) => Promise<IMultiSearchDropdownOption[]>;
  minSearchLength?: number;
  debounceMs?: number;
  /**
   * Optional list of options that should be pre-selected (useful when the form loads
   * existing selections from the backend).
   *
   * NOTE: values from this list should also be present in the `values` prop for the
   * tags to show up.
   */
  initialSelectedOptions?: IMultiSearchDropdownOption[];
}

/**
 * Wrapper component for MultiSearchDropdown that handles API search logic internally.
 *
 * This component manages:
 * - Debounced API search with useApiSearch hook
 * - Persisting selected items even when search results change
 * - Showing helpful placeholder messages based on search state
 * - Loading and error states
 *
 * @example
 * ```typescript
 * <MultiSearchDropdownApi
 *   fieldId="country-search"
 *   values={selectedCountries}
 *   onValueChange={setSelectedCountries}
 *   searchFn={async (term, signal) => {
 *     const response = await api.searchCountries(term, { signal });
 *     return response.map(c => ({ value: c.id, label: c.name }));
 *   }}
 *   minSearchLength={3}
 *   debounceMs={1000}
 * />
 * ```
 */
export const MultiSearchDropdownApi: React.FC<IMultiSearchDropdownApiProps> = ({
  fieldId,
  values,
  onValueChange,
  label,
  className,
  inputClassName,
  maxVisibleItems,
  withTags = true,
  disabled = false,
  errorMessage,
  errorClassName,
  searchFn,
  minSearchLength = 3,
  debounceMs = 1000,
  inputPlaceholder,
  initialSelectedOptions = [],
}) => {
  // Store selected options so they persist even when search results change
  const [selectedOptionsMap, setSelectedOptionsMap] = useState<Record<string, IMultiSearchDropdownOption>>(() => {
    if (!initialSelectedOptions?.length) {
      return {};
    }
    return initialSelectedOptions.reduce<Record<string, IMultiSearchDropdownOption>>((acc, option) => {
      if (option?.value) {
        acc[option.value] = option;
      }
      return acc;
    }, {});
  });

  // Use the API search hook
  const { options, loading, error, searchText, setSearchText } = useApiSearch<IMultiSearchDropdownOption>(searchFn, {
    minLength: minSearchLength,
    debounceMs,
    enabled: !disabled,
  });

  // Whenever the parent provides new initial options, make sure they are stored
  // so the tags can render even before the user searches.
  useEffect(() => {
    if (!initialSelectedOptions?.length) {
      return;
    }
    setSelectedOptionsMap(prev => {
      const next = { ...prev };
      initialSelectedOptions.forEach(option => {
        if (option?.value) {
          next[option.value] = option;
        }
      });
      return next;
    });
  }, [initialSelectedOptions]);

  // Keep the selected options map in sync when the controlled values array changes externally
  useEffect(() => {
    setSelectedOptionsMap(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(value => {
        if (!values.includes(value)) {
          delete next[value];
        }
      });
      return next;
    });
  }, [values]);

  // Merge search results with selected items to ensure selected items always show in tags
  const allOptions = useMemo(() => {
    const optionsMap = new Map<string, IMultiSearchDropdownOption>();

    // Add all selected options first (these must always be available for tag rendering)
    Object.values(selectedOptionsMap).forEach(option => {
      optionsMap.set(option.value, option);
    });

    // Add current search results (may override selected if they appear in results)
    options.forEach(option => {
      optionsMap.set(option.value, option);
    });

    return Array.from(optionsMap.values());
  }, [options, selectedOptionsMap]);

  // Determine what to show in the dropdown based on search state
  // IMPORTANT: Always include selected options in the final array so tags can render,
  // but DON'T show them in the dropdown when user is actively searching
  const displayOptions = useMemo(() => {
    // Get selected options that should always be available for tag rendering
    const selectedOptions = Object.values(selectedOptionsMap);

    // If user hasn't typed anything, show instruction + selected options for tags
    if (searchText.length === 0) {
      // If there are no search results but we have selected items, just return selected items
      // Otherwise show the placeholder message in the dropdown
      return selectedOptions.length > 0 ? selectedOptions : [];
    }

    // If user hasn't typed enough, show minimum length message + selected options for tags
    if (searchText.length < minSearchLength) {
      // Include selected options for tag rendering, but they won't show in dropdown
      return selectedOptions.length > 0 ? [...selectedOptions] : [];
    }

    // User is actively searching (3+ chars): ONLY show API search results
    // Don't include selected items in the dropdown - they're already visible as tags
    // But we still need selected options available for tag rendering
    return [...options, ...selectedOptions.filter(selected => !options.some(opt => opt.value === selected.value))];
  }, [searchText, minSearchLength, options, selectedOptionsMap]);

  // Handle value changes and persist selected option data
  const handleValueChange = (newValues: string[]) => {
    onValueChange(newValues);

    // Update selected options map to persist the data
    const newSelectedMap = { ...selectedOptionsMap };

    // Add newly selected options to the map
    newValues.forEach(value => {
      if (!newSelectedMap[value]) {
        const option = allOptions.find(opt => opt.value === value);
        if (option) {
          newSelectedMap[value] = option;
        }
      }
    });

    // Remove deselected options from the map
    Object.keys(newSelectedMap).forEach(value => {
      if (!newValues.includes(value)) {
        delete newSelectedMap[value];
      }
    });

    setSelectedOptionsMap(newSelectedMap);
    setSearchText('');
  };

  return (
    <MultiSearchDropdown
      fieldId={fieldId}
      values={values}
      text={searchText}
      options={displayOptions}
      onValueChange={handleValueChange}
      onTextChange={setSearchText}
      label={label}
      className={className}
      inputClassName={inputClassName}
      maxVisibleItems={maxVisibleItems}
      withTags={withTags}
      disabled={disabled}
      loading={loading}
      errorMessage={errorMessage ?? error?.message}
      errorClassName={errorClassName}
      inputPlaceholder={inputPlaceholder}
      hideSelectedFromDropdown={true}
    />
  );
};
